import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { CycleAvailableDishesComponent } from "../CycleAvailableDishesComponent/CycleAvailableDishesComponent";
import ExcelJS from "exceljs";

import css from "./AvailableDishesComponent.module.css";
import { ButtonEdit, SliderCalendar } from "../../../ui";
import { CloneMenuComponent } from "../CloneMenuComponent/CloneMenuComponent";
import AvailableListDishesComponent from "../AvailableListDishesComponent/AvailableListDishesComponent";
import { SelectAvailableDishesModal } from "../menuSectionModals/SelectAvailableDishesModal/SelectAvailableDishesModal";
import { Button, Spin } from "antd";
import { MdDownload } from "react-icons/md";
import { appSettingsActions } from "../../../store/slices/settings-slices";

const AvailableDishesComponent = () => {
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [loadingXls, setLoadingXls] = useState(false);
  const { availableDishesArray, status, dataOfDay } = useSelector((store) => store.availableDishes);
  const { menuFormat, status: settingsStatus } = useSelector((store) => store.appSettings);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (!settingsStatus || settingsStatus === null) {
      dispatch(appSettingsActions.getAppSettings());
    }
  }, [dispatch, settingsStatus]);

  if (!settingsStatus || settingsStatus === "loading") {
    return <div style={{ display: "flex", justifyContent: "center", padding: "60px" }}><Spin size="large" /></div>;
  }

  if (menuFormat === "period_based") return <CycleAvailableDishesComponent />;

  const handleCreateClick = () => {
    setIsOpen(true);
  };

  const handleClickXlsAvailableDishes = async () => {
    try {
      setLoadingXls(true);
      const items = availableDishesArray || [];
      const itemsWithDish = items.filter((e) => e?.dish);

      if (itemsWithDish.length === 0) {
        alert(t("No dishes data available"));
        return;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "GWT ERP";
      workbook.lastModifiedBy = "GWT ERP";
      workbook.created = new Date();
      workbook.modified = new Date();

      const worksheet = workbook.addWorksheet("Dishes with Ingredients");

      worksheet.columns = [
        { key: "dishName", width: 50 },
        { key: "ingredientName", width: 45 },
        { key: "weight", width: 15 },
        { key: "weightNetto", width: 15 },
      ];

      const titleRow = worksheet.addRow(["Available dishes with ingredients on " + dataOfDay]);
      titleRow.font = { bold: true, size: 14 };
      worksheet.mergeCells("A1:D1");
      worksheet.getCell("A1").alignment = { horizontal: "center" };
      titleRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "E6F4FF" },
        };
      });

      const headerRow = worksheet.addRow([
        "Dish Name",
        "Ingredient Name",
        "Weight (g)",
        "Weight Netto (g)",
      ]);
      headerRow.font = { bold: true, color: { argb: "FFFFFF" } };
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4472C4" },
        };
        cell.alignment = { horizontal: "center" };
      });

      const currentLanguage = i18n.language;

      itemsWithDish.forEach((entry, index) => {
        const dish = entry.dish;

        const dishNameBase =
          dish.name?.[currentLanguage] ||
          dish.name?.en ||
          dish.name?.ru ||
          "Unknown Dish";
        const dateStr = entry.date
          ? new Date(entry.date).toISOString().split("T")[0]
          : "";
        const headerParts = [];
        if (dateStr) headerParts.push(`date: ${dateStr}`);
        if (typeof entry.menuDishesCount === "number") {
          headerParts.push(`menus: ${entry.menuDishesCount}`);
        }
        const dishName =
          headerParts.length > 0
            ? `${dishNameBase} (${headerParts.join(", ")})`
            : dishNameBase;

        const dishHeaderRow = worksheet.addRow({
          dishName,
          ingredientName: "",
          weight: "",
          weightNetto: "",
        });
        dishHeaderRow.font = { bold: true, size: 11 };
        dishHeaderRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFE5CC" },
          };
        });

        const ingredients = entry.ingredients || [];
        ingredients.forEach((ingredient) => {
          const nm = ingredient?.name;
          const ingredientName =
            nm?.[currentLanguage] ||
            nm?.en ||
            nm?.ru ||
            nm?.ge ||
            nm?.ar ||
            "Unknown Ingredient";
          const w =
            ingredient?.weight != null && ingredient?.weight !== ""
              ? ingredient.weight
              : "0";
          const wNetto =
            ingredient?.weightNetto != null && ingredient?.weightNetto !== ""
              ? ingredient.weightNetto
              : "0";
          const row = worksheet.addRow({
            dishName: "",
            ingredientName: ingredientName,
            weight: w,
            weightNetto: wNetto,
          });
          row.eachCell((cell) => {
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFFFF" },
            };
          });
        });

        if (index < itemsWithDish.length - 1) {
          worksheet.addRow({});
        }
      });

      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `available_dishes_with_ingredients_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting available dishes:", error);
      alert(t("Error exporting dishes. Please try again."));
    } finally {
      setLoadingXls(false);
    }
  };

  return (
    <section className={css.wrap}>
      <div className={css.blockCalendar}>
        <SliderCalendar />
      </div>

      <div className={css.blockBody}>
        <div className={css.blockSearch}>
          <ButtonEdit onClick={handleCreateClick} />
          <CloneMenuComponent />
          <Button
            type="dashed"
            icon={<MdDownload />}
            loading={loadingXls || status === 'Loading'}
            onClick={handleClickXlsAvailableDishes}
          >
            Export XLS Available Dishes
          </Button>
        </div>

        <div className={css.blockInfo}>
          <AvailableListDishesComponent />
        </div>
      </div>

      {isOpen && <SelectAvailableDishesModal setIsOpen={setIsOpen} />}
    </section>
  );
};

export { AvailableDishesComponent };
